import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { 
  FileText, Plus, Edit, Trash2, ChevronDown, ChevronRight, GripVertical,
  CheckCircle, XCircle, Scale, Hash, AlignLeft
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS_RESPOSTA = [
  { value: 'escala_5', label: 'Escala 1-5', icon: Scale },
  { value: 'escala_10', label: 'Escala 1-10', icon: Scale },
  { value: 'sim_nao', label: 'Sim/Não', icon: CheckCircle },
  { value: 'numerico', label: 'Numérico', icon: Hash },
  { value: 'texto', label: 'Texto', icon: AlignLeft }
];

export default function FormBuilderPage() {
  const [formularios, setFormularios] = useState([]);
  const [selectedFormulario, setSelectedFormulario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDimensoes, setExpandedDimensoes] = useState({});
  
  // Modals
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [dimModalOpen, setDimModalOpen] = useState(false);
  const [critModalOpen, setCritModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Forms
  const [formData, setFormData] = useState({ nome: '', descricao: '', versao: '1.0', ativo: true });
  const [dimData, setDimData] = useState({ codigo: '', nome: '', descricao: '', peso: 0, ordem: 1 });
  const [critData, setCritData] = useState({ 
    codigo: '', questao: '', tipo_resposta: 'escala_5', peso: 0, ordem: 1, ajuda: '', obrigatorio: true 
  });
  const [selectedDimensao, setSelectedDimensao] = useState(null);

  useEffect(() => {
    loadFormularios();
  }, []);

  const loadFormularios = async () => {
    try {
      const response = await apiClient.get('/visitare/formularios');
      setFormularios(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar formulários');
    } finally {
      setLoading(false);
    }
  };

  const loadFormulario = async (id) => {
    try {
      const response = await apiClient.get(`/visitare/formularios/${id}`);
      setSelectedFormulario(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar formulário');
    }
  };

  const handleCreateFormulario = async () => {
    try {
      if (editMode && selectedFormulario) {
        await apiClient.put(`/visitare/formularios/${selectedFormulario.id}`, formData);
        toast.success('Formulário atualizado');
      } else {
        const response = await apiClient.post('/visitare/formularios', formData);
        toast.success('Formulário criado');
      }
      setFormModalOpen(false);
      loadFormularios();
      if (selectedFormulario) loadFormulario(selectedFormulario.id);
    } catch (error) {
      toast.error('Erro ao guardar formulário');
    }
  };

  const handleDeleteFormulario = async (id) => {
    if (!window.confirm('Desativar este formulário?')) return;
    try {
      await apiClient.delete(`/visitare/formularios/${id}`);
      toast.success('Formulário desativado');
      loadFormularios();
      if (selectedFormulario?.id === id) setSelectedFormulario(null);
    } catch (error) {
      toast.error('Erro ao desativar');
    }
  };

  const handleCreateDimensao = async () => {
    if (!selectedFormulario) return;
    try {
      if (editMode && selectedDimensao) {
        await apiClient.put(`/visitare/formularios/${selectedFormulario.id}/dimensoes/${selectedDimensao.id}`, dimData);
        toast.success('Dimensão atualizada');
      } else {
        await apiClient.post(`/visitare/formularios/${selectedFormulario.id}/dimensoes`, dimData);
        toast.success('Dimensão adicionada');
      }
      setDimModalOpen(false);
      loadFormulario(selectedFormulario.id);
    } catch (error) {
      toast.error('Erro ao guardar dimensão');
    }
  };

  const handleDeleteDimensao = async (dimId) => {
    if (!window.confirm('Remover dimensão e todos os critérios?')) return;
    try {
      await apiClient.delete(`/visitare/formularios/${selectedFormulario.id}/dimensoes/${dimId}`);
      toast.success('Dimensão removida');
      loadFormulario(selectedFormulario.id);
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const handleCreateCriterio = async () => {
    if (!selectedDimensao) return;
    try {
      if (editMode) {
        await apiClient.put(`/visitare/criterios/${critData.id}`, critData);
        toast.success('Critério atualizado');
      } else {
        await apiClient.post(`/visitare/dimensoes/${selectedDimensao.id}/criterios`, critData);
        toast.success('Critério adicionado');
      }
      setCritModalOpen(false);
      loadFormulario(selectedFormulario.id);
    } catch (error) {
      toast.error('Erro ao guardar critério');
    }
  };

  const handleDeleteCriterio = async (critId) => {
    if (!window.confirm('Remover este critério?')) return;
    try {
      await apiClient.delete(`/visitare/criterios/${critId}`);
      toast.success('Critério removido');
      loadFormulario(selectedFormulario.id);
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const openFormModal = (form = null) => {
    if (form) {
      setFormData({ nome: form.nome, descricao: form.descricao || '', versao: form.versao, ativo: form.ativo });
      setEditMode(true);
    } else {
      setFormData({ nome: '', descricao: '', versao: '1.0', ativo: true });
      setEditMode(false);
    }
    setFormModalOpen(true);
  };

  const openDimModal = (dim = null) => {
    if (dim) {
      setDimData({ codigo: dim.codigo, nome: dim.nome, descricao: dim.descricao || '', peso: dim.peso, ordem: dim.ordem });
      setSelectedDimensao(dim);
      setEditMode(true);
    } else {
      const nextOrdem = (selectedFormulario?.dimensoes?.length || 0) + 1;
      setDimData({ codigo: '', nome: '', descricao: '', peso: 0, ordem: nextOrdem });
      setSelectedDimensao(null);
      setEditMode(false);
    }
    setDimModalOpen(true);
  };

  const openCritModal = (dim, crit = null) => {
    setSelectedDimensao(dim);
    if (crit) {
      setCritData({ ...crit });
      setEditMode(true);
    } else {
      const nextOrdem = (dim.criterios?.length || 0) + 1;
      setCritData({ codigo: '', questao: '', tipo_resposta: 'escala_5', peso: 0, ordem: nextOrdem, ajuda: '', obrigatorio: true });
      setEditMode(false);
    }
    setCritModalOpen(true);
  };

  const toggleDimensao = (dimId) => {
    setExpandedDimensoes(prev => ({ ...prev, [dimId]: !prev[dimId] }));
  };

  const calcPesoTotal = (items) => items?.reduce((sum, i) => sum + (i.peso || 0), 0) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="form-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Form Builder</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Criar e gerir formulários de avaliação para visitas</p>
        </div>
        <Button onClick={() => openFormModal()} className="bg-[#0066CC] hover:bg-[#0052a3]">
          <Plus className="w-4 h-4 mr-2" />
          Novo Formulário
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Formulários */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Formulários</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {formularios.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Nenhum formulário criado</p>
            ) : (
              formularios.map(form => (
                <div
                  key={form.id}
                  onClick={() => loadFormulario(form.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedFormulario?.id === form.id 
                      ? 'border-[#0066CC] bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#0066CC]" />
                      <span className="font-medium text-sm">{form.nome}</span>
                    </div>
                    <Badge variant={form.ativo ? 'default' : 'secondary'} className="text-xs">
                      {form.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">v{form.versao} • {form.num_dimensoes || 0} dimensões</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Editor do Formulário */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {selectedFormulario ? selectedFormulario.nome : 'Selecione um formulário'}
              </CardTitle>
              {selectedFormulario && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openFormModal(selectedFormulario)}>
                    <Edit className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteFormulario(selectedFormulario.id)} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedFormulario ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Selecione ou crie um formulário para começar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Info */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                  <p className="text-slate-600 dark:text-slate-300">{selectedFormulario.descricao || 'Sem descrição'}</p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>Versão: {selectedFormulario.versao}</span>
                    <span>Peso Total: {calcPesoTotal(selectedFormulario.dimensoes)}%</span>
                    {calcPesoTotal(selectedFormulario.dimensoes) !== 100 && (
                      <span className="text-amber-600">⚠ Peso deve totalizar 100%</span>
                    )}
                  </div>
                </div>

                {/* Dimensões */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Dimensões</h3>
                  <Button size="sm" onClick={() => openDimModal()}>
                    <Plus className="w-4 h-4 mr-1" /> Dimensão
                  </Button>
                </div>

                <div className="space-y-2">
                  {selectedFormulario.dimensoes?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Adicione dimensões ao formulário</p>
                  ) : (
                    selectedFormulario.dimensoes?.map(dim => (
                      <div key={dim.id} className="border rounded-lg dark:border-slate-700">
                        {/* Dimensão Header */}
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                          onClick={() => toggleDimensao(dim.id)}
                        >
                          <div className="flex items-center gap-2">
                            {expandedDimensoes[dim.id] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="font-medium">{dim.codigo} - {dim.nome}</span>
                            <Badge variant="outline" className="text-xs">{dim.peso}%</Badge>
                          </div>
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => openCritModal(dim)}>
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openDimModal(dim)}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteDimensao(dim.id)} className="text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Critérios */}
                        {expandedDimensoes[dim.id] && (
                          <div className="border-t dark:border-slate-700 p-3 bg-slate-50/50 dark:bg-slate-800/50">
                            {dim.criterios?.length === 0 ? (
                              <p className="text-xs text-slate-500 text-center py-2">Sem critérios</p>
                            ) : (
                              <div className="space-y-2">
                                {dim.criterios?.map(crit => (
                                  <div key={crit.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 rounded border dark:border-slate-700">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-slate-500">{crit.codigo}</span>
                                        <Badge variant="outline" className="text-xs">{crit.tipo_resposta}</Badge>
                                        <Badge variant="outline" className="text-xs">{crit.peso}%</Badge>
                                      </div>
                                      <p className="text-sm mt-1">{crit.questao}</p>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button variant="ghost" size="sm" onClick={() => openCritModal(dim, crit)}>
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCriterio(crit.id)} className="text-red-600">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="mt-2 text-xs text-slate-500">
                              Peso total critérios: {calcPesoTotal(dim.criterios)}%
                              {calcPesoTotal(dim.criterios) !== 100 && (
                                <span className="text-amber-600 ml-2">⚠ Deve totalizar 100%</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal Formulário */}
      <Dialog open={formModalOpen} onOpenChange={setFormModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editar Formulário' : 'Novo Formulário'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input value={formData.nome} onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))} placeholder="Nome do formulário" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={formData.descricao} onChange={e => setFormData(p => ({ ...p, descricao: e.target.value }))} placeholder="Descrição" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Versão</Label>
                <Input value={formData.versao} onChange={e => setFormData(p => ({ ...p, versao: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={formData.ativo} onChange={e => setFormData(p => ({ ...p, ativo: e.target.checked }))} className="w-4 h-4" />
                <Label>Ativo</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateFormulario} className="bg-[#0066CC]">{editMode ? 'Guardar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Dimensão */}
      <Dialog open={dimModalOpen} onOpenChange={setDimModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editar Dimensão' : 'Nova Dimensão'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input value={dimData.codigo} onChange={e => setDimData(p => ({ ...p, codigo: e.target.value }))} placeholder="GO" />
              </div>
              <div>
                <Label>Ordem</Label>
                <Input type="number" min="1" value={dimData.ordem} onChange={e => setDimData(p => ({ ...p, ordem: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            <div>
              <Label>Nome *</Label>
              <Input value={dimData.nome} onChange={e => setDimData(p => ({ ...p, nome: e.target.value }))} placeholder="Gestão Organizacional" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={dimData.descricao} onChange={e => setDimData(p => ({ ...p, descricao: e.target.value }))} />
            </div>
            <div>
              <Label>Peso (%) *</Label>
              <Input type="number" min="0" max="100" value={dimData.peso} onChange={e => setDimData(p => ({ ...p, peso: parseFloat(e.target.value) || 0 }))} />
              <p className="text-xs text-slate-500 mt-1">Soma dos pesos de todas as dimensões deve ser 100%</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDimModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateDimensao} className="bg-[#0066CC]">{editMode ? 'Guardar' : 'Adicionar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Critério */}
      <Dialog open={critModalOpen} onOpenChange={setCritModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editar Critério' : 'Novo Critério'}</DialogTitle>
            <DialogDescription>{selectedDimensao?.nome}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input value={critData.codigo} onChange={e => setCritData(p => ({ ...p, codigo: e.target.value }))} placeholder="GO1" />
              </div>
              <div>
                <Label>Ordem</Label>
                <Input type="number" min="1" value={critData.ordem} onChange={e => setCritData(p => ({ ...p, ordem: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            <div>
              <Label>Questão *</Label>
              <Textarea value={critData.questao} onChange={e => setCritData(p => ({ ...p, questao: e.target.value }))} placeholder="A instituição possui plano estratégico?" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Resposta *</Label>
                <Select value={critData.tipo_resposta} onValueChange={v => setCritData(p => ({ ...p, tipo_resposta: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_RESPOSTA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Peso (%) *</Label>
                <Input type="number" min="0" max="100" value={critData.peso} onChange={e => setCritData(p => ({ ...p, peso: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>
            <div>
              <Label>Texto de Ajuda</Label>
              <Input value={critData.ajuda} onChange={e => setCritData(p => ({ ...p, ajuda: e.target.value }))} placeholder="Dica para o visitador" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={critData.obrigatorio} onChange={e => setCritData(p => ({ ...p, obrigatorio: e.target.checked }))} className="w-4 h-4" />
              <Label>Obrigatório</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCritModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateCriterio} className="bg-[#0066CC]">{editMode ? 'Guardar' : 'Adicionar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
